import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createErrorBanner } from 'components/Banner';
import { Dropdown, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import Empty from 'components/Empty';
import FeatureNotSupported from 'components/FeatureNotSupported';
import { FlyoutContext, FlyoutProvider } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import PageTurner from 'components/PageTurner/PageTurner';
import { Table } from 'components/Table';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import { ICONS, NAV_ICONS } from 'constants/icons';
import { Title } from 'features/App';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { bem } from 'helpers/class-name';
import { IA_KEY } from 'helpers/ia-logic';
import { formatEmailSubjectDateTime } from 'helpers/intl-fns';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectIaLogic } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import { SKU_ANALYTICS_EVENTS, SKU_ORDER_ACTION_SURFACE } from '../Items/SkuAnalytics.constants';
import { STATUS_TO_BOOLEANS } from '../Items/SkuItems.constants';
import { fetchSkuCatalogItem, updateSkuItemAvailability } from '../Items/SkuItems.helpers';
import { getItemAvailabilityStatus } from '../Items/SkuMenuData.normalizers';
import { getSkuVenueSource } from '../Items/SkuVenueSource.helpers';
import { skuMenuKeys } from '../Items/skuMenuKeys';
import SkuOrderFlyout, { SKU_ORDER_FLYOUT_ID } from '../shared/SkuOrderFlyout';
import SkuSettingsGearButton from '../shared/SkuSettingsGearButton';
import SkuVenueOpenStatus from '../shared/SkuVenueOpenStatus';

import { buildCancelMessageHtml, mapOrderToTableRow } from './SkuOrderDashboard.helpers';
import messages from './SkuOrderDashboard.messages';
import SkuOrderLifecycleStatusFilter from './SkuOrderLifecycleStatusFilter';
import { SKU_AVAILABILITY_MODAL_NAME } from './SkuAvailabilityModal';
import skuCancelConfirmMessages from './SkuCancelConfirmModal.messages';
import { SKU_MISSED_PICKUP_MODAL_NAME } from './SkuMissedPickupModal';
import './SkuOrderDashboard.scss';
import OrderStatusCell from './SkuOrderStatusCell';
import { OrderTableHeader, MobileOrderTableHeader, CellValue } from './SkuOrderTable';
import { ORDER_ITEM_CANCELLATION_TYPE, PARTNER_ORDERS_TAB } from './SkuOrders.constants';
import {
  useApplyOptimisticOrderCancelled,
  useCancelSkuOrder,
  useFulfillSkuOrder,
  useInvalidatePartnerOrders,
  usePartnerOrders,
} from './SkuOrders.hooks';

const { block, element } = bem('sku-orders-dashboard');

const ItemsCell = ({ groups }) => {
  if (!groups?.length) return <CellValue value={null} />;

  return (
    <span className={element('items-cell')}>
      {groups.map((group, idx) => (
        <React.Fragment key={group.itemId}>
          {idx > 0 && ', '}
          <span className={element('items-cell-name', { 'crossed-out': group.isCrossedOut })}>
            {group.count > 1 ? `${group.name} (x${group.count})` : group.name}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};

ItemsCell.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      isCrossedOut: PropTypes.bool.isRequired,
    })
  ),
};

const TAB_TODAY = PARTNER_ORDERS_TAB.TODAY;
const TAB_HISTORY = PARTNER_ORDERS_TAB.HISTORY;

const SkuOrderDashboardContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const activeVenue = useSelector(selectActiveVenue);
  const { venueId, timezone: venueTimezone, venueAliasUrl, shareUrlConsumerVenue, venueName } = activeVenue;
  const source = getSkuVenueSource(activeVenue);
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_ORDERS, venueId));
  const isDesktop = useSelector(selectIsDesktop);
  const [activeTab, setActiveTab] = useState(TAB_TODAY);
  const [historyLifecycleStatus, setHistoryLifecycleStatus] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [fulfillingOrderId, setFulfillingOrderId] = useState(null);

  const { openFlyout } = useContext(FlyoutContext);
  const invalidatePartnerOrders = useInvalidatePartnerOrders();
  const applyOptimisticOrderCancelled = useApplyOptimisticOrderCancelled();

  const { orders, isLoading, isError, page, changePage, nextPageCursor, showPagination, startNumber, endNumber } =
    usePartnerOrders(activeTab, {
      lifecycleStatus: activeTab === TAB_HISTORY ? historyLifecycleStatus : null,
      onError: () => {
        dispatch(createErrorBanner(intl.formatMessage(messages.loadOrdersError)));
      },
    });

  const { mutate: cancelOrder } = useCancelSkuOrder();

  const { mutate: fulfillOrder } = useFulfillSkuOrder({
    onSuccess: (_data, orderId) => {
      Segment.event(SKU_ANALYTICS_EVENTS.ORDER_MARKED_AS_FULFILLED, { venueId, source, orderId });
      invalidatePartnerOrders();
      setFulfillingOrderId(null);
    },
    onError: () => {
      dispatch(createErrorBanner(intl.formatMessage(messages.fulfillOrderError)));
      setFulfillingOrderId(null);
    },
  });

  useEffect(() => {
    if (!featureIsSupported || !venueId) return;
    Segment.event(SKU_ANALYTICS_EVENTS.ORDER_MANAGEMENT_PAGE_OPENED, { venueId, source });
  }, [featureIsSupported, venueId, source]);

  useEffect(() => {
    if (!featureIsSupported || !venueId || activeTab !== TAB_HISTORY) return;
    Segment.event(SKU_ANALYTICS_EVENTS.ORDER_HISTORY_VIEWED, { venueId, source });
  }, [activeTab, featureIsSupported, venueId, source]);

  const handleFulfill = useCallback(
    orderId => {
      setFulfillingOrderId(orderId);
      fulfillOrder(orderId);
    },
    [fulfillOrder]
  );

  const openCancelModal = useCallback(
    (orderId, cancellationType, { modalProps = {}, outOfStockItemUpdate = null } = {}) => {
      const order = orders.find(o => o?.core?.orderId === orderId) || null;
      if (!order) return;

      const orderCreatedOn = order?.core?.createdOn;
      const activeVenueName = venueName || '';
      const userName = order?.userName || '';

      const subjectValues = orderCreatedOn
        ? { ...formatEmailSubjectDateTime(orderCreatedOn, venueTimezone, intl), venueName: activeVenueName }
        : { time: '', date: '', venueName: activeVenueName };

      const subject = intl.formatMessage(messages.cancelOrderSubject, { venueName: activeVenueName });
      const messageHtml = buildCancelMessageHtml(intl, {
        time: subjectValues.time,
        date: subjectValues.date,
        venueName: activeVenueName,
        userName,
        venueAliasUrl,
        shareUrlConsumerVenue,
        creditAmountTotal: order?.creditAmountTotal,
      });

      dispatch(
        openModal('SkuCancelConfirmModal', {
          hasCloseButton: true,
          onRequestClose: () => dispatch(closeModal()),
          onSubmit: async (_values, { resolveSubmit, rejectSubmit }) => {
            if (outOfStockItemUpdate) {
              try {
                await outOfStockItemUpdate.apply();
              } catch (error) {
                rejectSubmit();
                dispatch(closeModal());
                dispatch(createErrorSnackbar(messages.cancelOrderError, {}, {}, getApiErrorSnackbarDescription(error)));
                return;
              }
            }
            cancelOrder(
              { orderId, cancellationType },
              {
                onSuccess: () => {
                  Segment.event(SKU_ANALYTICS_EVENTS.ORDER_CANCELLED, {
                    venueId,
                    source,
                    orderId,
                    cancellationType,
                  });
                  resolveSubmit();
                  dispatch(closeModal());
                  applyOptimisticOrderCancelled(orderId, cancellationType);
                  dispatch(createSuccessSnackbar(messages.cancelOrderSuccessWithNotification));
                },
                onError: async error => {
                  if (outOfStockItemUpdate) {
                    try {
                      await outOfStockItemUpdate.revert();
                      queryClient.invalidateQueries({ queryKey: skuMenuKeys.all });
                    } catch (_error) {
                      // Revert is best-effort; cancel failure toast is shown either way.
                    }
                  }
                  rejectSubmit();
                  dispatch(closeModal());
                  dispatch(
                    createErrorSnackbar(messages.cancelOrderError, {}, {}, getApiErrorSnackbarDescription(error))
                  );
                },
              }
            );
          },
          title: <Message message={messages.cancelOrderModalTitle} />,
          to: userName,
          subject,
          initialValues: { message: messageHtml },
          ...modalProps,
        })
      );
    },
    [
      dispatch,
      intl,
      venueTimezone,
      venueAliasUrl,
      shareUrlConsumerVenue,
      venueName,
      orders,
      cancelOrder,
      applyOptimisticOrderCancelled,
      queryClient,
      venueId,
      source,
    ]
  );

  const handleCancelOrder = useCallback(
    orderId => openCancelModal(orderId, ORDER_ITEM_CANCELLATION_TYPE.PARTNER_CANCELLED),
    [openCancelModal]
  );

  const handleCustomerMissedPickup = useCallback(
    (orderId, { surface } = {}) => {
      dispatch(
        openModal(SKU_MISSED_PICKUP_MODAL_NAME, {
          onCancel: () => dispatch(closeModal()),
          onConfirm: () => {
            const cancellationType = ORDER_ITEM_CANCELLATION_TYPE.USER_MISSED_PICKUP;
            dispatch(closeModal());
            cancelOrder(
              { orderId, cancellationType },
              {
                onSuccess: () => {
                  Segment.event(SKU_ANALYTICS_EVENTS.USER_MARKED_AS_MISSED_PICKUP, {
                    venueId,
                    source,
                    orderId,
                    surface,
                  });
                  Segment.event(SKU_ANALYTICS_EVENTS.ORDER_CANCELLED, {
                    venueId,
                    source,
                    orderId,
                    cancellationType,
                  });
                  applyOptimisticOrderCancelled(orderId, cancellationType);
                  dispatch(createSuccessSnackbar(messages.cancelOrderSuccess));
                },
                onError: error => {
                  dispatch(
                    createErrorSnackbar(messages.cancelOrderError, {}, {}, getApiErrorSnackbarDescription(error))
                  );
                },
              }
            );
          },
        })
      );
    },
    [dispatch, cancelOrder, applyOptimisticOrderCancelled, venueId, source]
  );

  const openItemOutOfStockModal = useCallback(
    (orderId, itemId, itemNameOverride) => {
      const order = orders.find(o => o?.core?.orderId === orderId) || null;

      // sku-525: Picking the first item is a temporary rule for the order-level out-of-stock
      // buttons. Once designs are finalised for that flow, replace this with the proper item selection.
      const targetOrderItem = itemId
        ? order?.orderItems?.find(i => i.core?.itemSummary?.itemId === itemId)
        : order?.orderItems?.find(i => i.core?.itemSummary?.itemId);

      const resolvedItemId = targetOrderItem?.core?.itemSummary?.itemId || null;
      const resolvedItemName = itemNameOverride || targetOrderItem?.core?.itemSummary?.name || '';

      function openAvailabilityModal() {
        dispatch(
          openModal(SKU_AVAILABILITY_MODAL_NAME, {
            itemName: resolvedItemName,
            onConfirm: selectedStatus => {
              dispatch(closeModal());

              // Out-of-stock still updates item availability today, then cancels via cancelOrder.
              // sku-358 adds cancelOrderItems; sku-360 should call that instead of cancelOrder and
              // drop this separate updateSkuItem call, as the new endpoint will cover both availability and cancellation.
              let availabilitySnapshot = null;

              const outOfStockItemUpdate =
                resolvedItemId && STATUS_TO_BOOLEANS[selectedStatus]
                  ? {
                      apply: async () => {
                        const priorCatalogItem = await fetchSkuCatalogItem(venueId, resolvedItemId, {
                          queryClient,
                        });
                        const priorStatus = getItemAvailabilityStatus(priorCatalogItem);
                        availabilitySnapshot = {
                          priorCatalogItem,
                          priorStatus,
                        };

                        await updateSkuItemAvailability(venueId, resolvedItemId, selectedStatus, {
                          queryClient,
                          catalogItem: priorCatalogItem,
                          nameFallback: resolvedItemName,
                        });
                        if (priorStatus !== selectedStatus) {
                          Segment.event(SKU_ANALYTICS_EVENTS.ITEM_AVAILABILITY_CHANGED, {
                            venueId,
                            itemId: resolvedItemId,
                            from: priorStatus,
                            to: selectedStatus,
                            source,
                          });
                        }
                        Segment.event(SKU_ANALYTICS_EVENTS.ITEM_MARKED_OUT_OF_STOCK, {
                          venueId,
                          source,
                          orderId,
                          itemId: resolvedItemId,
                          from: priorStatus,
                          to: selectedStatus,
                        });
                      },
                      revert: async () => {
                        if (!availabilitySnapshot?.priorStatus || availabilitySnapshot.priorStatus === selectedStatus) {
                          return;
                        }
                        await updateSkuItemAvailability(venueId, resolvedItemId, availabilitySnapshot.priorStatus, {
                          queryClient,
                          catalogItem: availabilitySnapshot.priorCatalogItem,
                          nameFallback: resolvedItemName,
                        });
                      },
                    }
                  : null;

              openCancelModal(orderId, ORDER_ITEM_CANCELLATION_TYPE.PARTNER_SOLD_OUT, {
                modalProps: {
                  onCancel: () => {
                    dispatch(closeModal());
                    openAvailabilityModal();
                  },
                  cancelMessage: skuCancelConfirmMessages.back,
                },
                outOfStockItemUpdate,
              });
            },
            onCancel: () => dispatch(closeModal()),
          })
        );
      }

      openAvailabilityModal();
    },
    [dispatch, orders, openCancelModal, queryClient, venueId, source]
  );

  const handleItemOutOfStock = openItemOutOfStockModal;

  const ordersForTab = useMemo(
    () =>
      orders.map(order =>
        mapOrderToTableRow(order, {
          intl,
          venueTimezone,
          locale: intl.locale,
        })
      ),
    [orders, intl, venueTimezone]
  );

  const handleRowClick = useCallback(
    orderId => {
      setSelectedOrderId(orderId);
      openFlyout(SKU_ORDER_FLYOUT_ID);
      Segment.event(SKU_ANALYTICS_EVENTS.ORDER_FLYOUT_OPENED, { venueId, source, orderId });
    },
    [openFlyout, venueId, source]
  );

  const selectedOrder = useMemo(
    () => orders.find(o => o?.core?.orderId === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  if (!featureIsSupported) {
    return (
      <>
        <Message message={messages.pageTitle} as={Title} />
        <FeatureNotSupported />
      </>
    );
  }

  const showEmpty = !isLoading && !isError && ordersForTab.length === 0;

  return (
    <>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <div className={element('tabs-header')}>
          <div className={element('tab-controls')}>
            <SkuVenueOpenStatus />
            <Dropdown
              attachment="right"
              className={element('settings-dropdown-wrapper')}
              contentClassName={element('settings-dropdown-content')}>
              {({ toggleMenu, closeMenu }) => (
                <>
                  <SkuSettingsGearButton ariaLabel={intl.formatMessage(messages.settingsButton)} onClick={toggleMenu} />
                  <DropdownMenu className={element('settings-dropdown')}>
                    <DropdownItem
                      onClick={() => {
                        Segment.event('Click Manage Items', { venueId, type: 'venue' });
                        navigate(makePath.items(venueId));
                        closeMenu();
                      }}>
                      <span className={element('settings-menu-item')}>
                        <Isvg src={ICONS.editPencilSku} className={element('settings-menu-icon')} />
                        <Message message={messages.manageItems} />
                      </span>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        Segment.event('Click Business Profile', { venueId, type: 'venue' });
                        navigate(makePath.businessProfile(venueId));
                        closeMenu();
                      }}>
                      <span className={element('settings-menu-item')}>
                        <Isvg src={ICONS.storefront} className={element('settings-menu-icon')} />
                        <Message message={messages.businessProfile} />
                      </span>
                    </DropdownItem>
                  </DropdownMenu>
                </>
              )}
            </Dropdown>
          </div>
          <Tabs selectedTab={activeTab} onTabChange={setActiveTab}>
            <Tab name={TAB_TODAY}>
              <TabLabel>
                <Message message={messages.tabToday} />
              </TabLabel>
            </Tab>
            <Tab name={TAB_HISTORY}>
              <TabLabel>
                <Message message={messages.tabHistory} />
              </TabLabel>
            </Tab>
          </Tabs>
        </div>

        {activeTab === TAB_HISTORY && (
          <SkuOrderLifecycleStatusFilter value={historyLifecycleStatus} onChange={setHistoryLifecycleStatus} />
        )}

        {showEmpty ? (
          <Empty
            icon={NAV_ICONS.appointments}
            title={messages.emptyOrders}
            subtitle={{ ...messages.emptyOrdersSubtitle, values: { br: <br /> } }}
          />
        ) : (
          <>
            <Table
              header={isDesktop ? <OrderTableHeader /> : <MobileOrderTableHeader />}
              emptyMessage={messages.emptyOrders}
              isLoading={isLoading}>
              {ordersForTab.map(order =>
                isDesktop ? (
                  <tr
                    key={order.id}
                    className={element('order-row')}
                    onClick={() => handleRowClick(order.rawOrderId)}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(order.rawOrderId);
                    }}>
                    <td>
                      <CellValue value={order.memberName} />
                    </td>
                    <td>
                      <CellValue value={order.id} />
                    </td>
                    <td>
                      <ItemsCell groups={order.itemGroups} />
                    </td>
                    <td>
                      <CellValue value={order.payableAmountDisplay} align="right" />
                    </td>
                    <td>
                      <CellValue value={order.pickupDate} align="right" />
                    </td>
                    <td>
                      <CellValue value={order.pickupTime} align="right" />
                    </td>
                    <td>
                      <OrderStatusCell
                        lifecycleStatus={order.lifecycleStatus}
                        showRowActions={isDesktop}
                        onFulfill={() => handleFulfill(order.rawOrderId)}
                        isFulfilling={fulfillingOrderId === order.rawOrderId}
                        onEditItem={() => handleRowClick(order.rawOrderId)}
                        onCancelOrder={() => handleCancelOrder(order.rawOrderId)}
                        onItemOutOfStock={() => handleItemOutOfStock(order.rawOrderId)}
                        onCustomerMissedPickup={() =>
                          handleCustomerMissedPickup(order.rawOrderId, {
                            surface: SKU_ORDER_ACTION_SURFACE.ACTIVE_ORDER_MENU,
                          })
                        }
                        orderId={order.rawOrderId}
                        userName={order.memberName}
                        userId={order.userId}
                        venueName={venueName}
                        orderCreatedOn={order.createdOn}
                        isInTodayTab={activeTab === TAB_TODAY}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={order.id}
                    className={element('order-row')}
                    onClick={() => handleRowClick(order.rawOrderId)}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(order.rawOrderId);
                    }}>
                    <td>
                      <CellValue value={order.memberName} />
                    </td>
                    <td>
                      <OrderStatusCell
                        lifecycleStatus={order.lifecycleStatus}
                        showRowActions={isDesktop}
                        onFulfill={() => handleFulfill(order.rawOrderId)}
                        isFulfilling={fulfillingOrderId === order.rawOrderId}
                        onEditItem={() => handleRowClick(order.rawOrderId)}
                        onCancelOrder={() => handleCancelOrder(order.rawOrderId)}
                        onItemOutOfStock={() => handleItemOutOfStock(order.rawOrderId)}
                        onCustomerMissedPickup={() =>
                          handleCustomerMissedPickup(order.rawOrderId, {
                            surface: SKU_ORDER_ACTION_SURFACE.ACTIVE_ORDER_MENU,
                          })
                        }
                        orderId={order.rawOrderId}
                        userName={order.memberName}
                        userId={order.userId}
                        venueName={venueName}
                        orderCreatedOn={order.createdOn}
                        isInTodayTab={activeTab === TAB_TODAY}
                      />
                    </td>
                  </tr>
                )
              )}
            </Table>
            {showPagination && (
              <PageTurner
                className="sku-orders-page-turner"
                endNumber={endNumber}
                isLoading={isLoading}
                isOnFirstPage={page === 1}
                isOnLastPage={!nextPageCursor}
                startNumber={startNumber}
                viewNextPage={() => changePage(page + 1)}
                viewPrevPage={() => changePage(page - 1)}
              />
            )}
          </>
        )}
      </div>

      <SkuOrderFlyout
        order={selectedOrder ?? undefined}
        onFulfill={selectedOrder?.core?.orderId ? () => handleFulfill(selectedOrder.core.orderId) : undefined}
        isFulfilling={fulfillingOrderId === selectedOrder?.core?.orderId}
        onCancelOrder={selectedOrder?.core?.orderId ? () => handleCancelOrder(selectedOrder.core.orderId) : undefined}
        onItemOutOfStock={
          selectedOrder?.core?.orderId
            ? (itemName, itemId) => handleItemOutOfStock(selectedOrder.core.orderId, itemId, itemName)
            : undefined
        }
        onCustomerMissedPickup={
          selectedOrder?.core?.orderId
            ? () =>
                handleCustomerMissedPickup(selectedOrder.core.orderId, {
                  surface: SKU_ORDER_ACTION_SURFACE.ORDER_FLYOUT,
                })
            : undefined
        }
        isInTodayTab={activeTab === TAB_TODAY}
      />
    </>
  );
};

const SkuOrderDashboard = () => (
  <FlyoutProvider>
    <SkuOrderDashboardContent />
  </FlyoutProvider>
);

export default SkuOrderDashboard;
